import React from 'react';
import { useBedtimeContext } from '../../context/BedtimeContext';
import { familyVoicePhrase } from '../../copy/terminology';
import { ApiError } from '../../api/http';
import { Chip, StateMessage } from '../ui';
import { BEDTIME_PAIR_SHEET_ID } from './BedtimePairSheet';
import './BedtimePairChip.css';

const WARNING_ID = 'bedtime-pair-warning';

/**
 * Inline "{listener} with {voice}" chip for the browsing routes. It opens the
 * shared pair sheet; it never shows on management routes. Prerequisite
 * states (no listener, no ready voice, needs selection) render nothing here
 * because the page carries the one honest empty state for them.
 */
const BedtimePairChip: React.FC = () => {
  const context = useBedtimeContext();

  if (context.status === 'loading') {
    return <p className="bedtime-chip__status" role="status" aria-live="polite">Loading your bedtime choices…</p>;
  }
  if (context.status === 'error') {
    return (
      <StateMessage
        kind="error"
        title="We couldn’t load your bedtime choices"
        error={context.error as ApiError}
        retry={{ onClick: () => void context.refresh() }}
        testId="bedtime-chip-error"
      />
    );
  }
  if (context.status !== 'ready' && context.status !== 'missing_bookends') return null;

  const voice = context.voices.find((candidate) => candidate.voice_id === context.pair?.voiceId);
  const listener = context.listeners.find((candidate) => candidate.listener_id === context.pair?.listenerId);
  const listenerName = listener?.display_name || 'listener';
  const voiceDescription = familyVoicePhrase(voice?.voice_name || 'Family voice');
  const missing = context.status === 'missing_bookends';

  return (
    <div className="bedtime-chip">
      <Chip
        label={`${listenerName} with ${voiceDescription}`}
        description="Change listener and voice"
        haspopup="dialog"
        controls={BEDTIME_PAIR_SHEET_ID}
        expanded={context.pairDialogOpen}
        describedBy={missing ? WARNING_ID : undefined}
        onSelect={() => context.openPairDialog()}
      />
      {missing && <p id={WARNING_ID} className="bedtime-chip__warning">Needs a hello and goodnight</p>}
      {context.offline && (
        <p className="bedtime-chip__offline" title="Connect to refresh your family and stories">Bedtime · saved on device</p>
      )}
    </div>
  );
};

export default BedtimePairChip;
